import { useState } from "react";
import { SignalBadge } from "@/components/SignalBadge";
import { AnalysisCard } from "@/components/AnalysisCard";
import { LockedOverlay } from "@/components/LockedOverlay";
import { TechnicalCard } from "@/components/TechnicalCard";
import { ScalpChart } from "@/components/ScalpChart";
import { PriceAlerts } from "@/components/PriceAlerts";
import { PositionBias } from "@/components/PositionBias";
import { AgreementStrip } from "@/components/AgreementStrip";
import { fmt, dirColor } from "@/lib/format";
import { shareFacebook, SITE_URL } from "@/lib/share";
import {
  Activity, Landmark, LineChart, Target, CheckCircle2, Crosshair, ArrowUpRight, ArrowDownRight, Facebook, Clock,
} from "lucide-react";

const timeStamp = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const STEP_ICONS = [Activity, Landmark, LineChart, Target, CheckCircle2, Crosshair];

const LOCKED_CONTRIBS = [
  { code: "A", name: "High-impact driver", category: "labor", contribution: 1 },
  { code: "B", name: "High-impact driver", category: "inflation", contribution: -1 },
  { code: "C", name: "High-impact driver", category: "demand_growth", contribution: 1 },
];

export function SignalPage({ signal, loading, refreshKey }) {
  const [tf, setTf] = useState("15m");
  if (loading || !signal) return <Loading />;
  const s = signal;
  const gUp = s.gold_trend.direction === "UP";
  const locked = !s.is_pro;
  const L = "🔒 Unlock with Pro";

  const pipeline = [
    { title: "Economic Data Surprise", body: locked ? L : `Gold impact score ${s.macro_score}: each forecast is compared with the LAST ACTUAL print of that indicator (not consensus). Above 0 = data expected to run hotter/stronger than last time → bad for gold; below 0 = cooler/weaker → good for gold.` },
    { title: "What the Fed will likely do", body: locked ? `${s.fed_reaction.stance} — ${L}` : `${s.fed_reaction.stance} — ${s.fed_reaction.detail}` },
    { title: "Dollar & Interest Rates", body: `DXY ${s.drivers.DXY.value} (${s.drivers.DXY.direction}), 2Y ${s.drivers.US02Y.value}%, 10Y ${s.drivers.US10Y.value}%${locked ? "" : ` — market score ${s.market_score} (stronger dollar / higher yields = bad for gold).`}` },
    { title: "Gold Conclusion", body: `Gold bias ${locked ? "•••" : s.gold_bias} → ${s.action} with ${s.confidence}% confidence.` },
    { title: "H1 Confirmation", body: locked ? L : s.execution.h1_confirmation, testId: "h1-confirmation-step" },
    { title: "M15 Entry", body: locked ? L : s.execution.m15_entry, testId: "m15-entry-step" },
  ];

  return (
    <div className="flex flex-col gap-5 fade-up">
      <AgreementStrip signal={s} refreshKey={refreshKey} />
      <PositionBias refreshKey={refreshKey} />
      <TechnicalCard refreshKey={refreshKey} tf={tf} setTf={setTf} />
      <ScalpChart tf={tf} refreshKey={refreshKey} />
      <PriceAlerts refreshKey={refreshKey} />

      {/* hero signal */}
      <section className="glass rounded-3xl p-6 flex flex-col items-center text-center gap-3 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{ background: "url('https://images.unsplash.com/photo-1610375461369-d613b564f4c4?q=60&w=1200') center/cover" }} />
        <span className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280] font-mono-num z-10">Macro Bias · XAUUSD Master Signal</span>
        <SignalBadge action={s.action} confidence={s.confidence} />
        <div className="flex items-center gap-2 z-10">
          <span data-testid="signal-gold-price" className="font-mono-num text-3xl font-bold tracking-tight">${fmt(s.gold_price)}</span>
          <span className={`flex items-center text-sm font-mono-num ${dirColor(s.gold_trend.direction)}`}>
            {gUp ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}{s.gold_trend.change}
          </span>
        </div>
        <div data-testid="signal-timestamp" className="z-10 flex items-center gap-1.5 text-[10px] font-mono-num text-[#6B7280]">
          <Clock size={11} />
          <span>{s.action} since {timeStamp(s.action_since)}</span>
          {s.quote_updated_at && <span>· price {timeStamp(s.quote_updated_at)}</span>}
          {s.data_updated_at && <span>· macro data {timeStamp(s.data_updated_at)}</span>}
        </div>
        <button
          data-testid="share-signal-button"
          onClick={() => shareFacebook(SITE_URL, `XAUUSD AI Terminal signal: ${s.action} gold at $${fmt(s.gold_price)} with ${s.confidence}% confidence. AI-powered gold signals from US macro data 👇`)}
          className="z-10 mt-1 flex items-center gap-2 px-4 py-2 rounded-full bg-[#1877F2]/15 border border-[#1877F2]/40 text-[#4293ff] font-semibold text-xs hover:bg-[#1877F2]/25 transition-colors">
          <Facebook size={14} /> Share signal
        </button>
      </section>

      {/* key drivers strip */}
      <section className="grid grid-cols-3 gap-3">
        <DriverChip label="DXY" t={s.drivers.DXY} color="#6366F1" unit="" />
        <DriverChip label="US 2Y" t={s.drivers.US02Y} color="#3B82F6" unit="%" />
        <DriverChip label="US 10Y" t={s.drivers.US10Y} color="#8B5CF6" unit="%" />
      </section>

      {/* execution plan */}
      {locked ? (
        <section className="relative grid grid-cols-3 gap-3 overflow-hidden rounded-2xl">
          <PlanCell label="Entry" value="$••••" color="#F59E0B" testId="plan-entry" />
          <PlanCell label="Stop Loss" value="$••••" color="#EF4444" testId="plan-sl" />
          <PlanCell label="Take Profit" value="$••••" color="#10B981" testId="plan-tp" />
          <LockedOverlay label="Unlock Entry / SL / TP" />
        </section>
      ) : s.execution.entry != null && (
        <section className="grid grid-cols-3 gap-3">
          <PlanCell label="Entry" value={`$${fmt(s.execution.entry)}`} color="#F59E0B" testId="plan-entry" />
          <PlanCell label="Stop Loss" value={`$${fmt(s.execution.stop_loss)}`} color="#EF4444" testId="plan-sl" />
          <PlanCell label="Take Profit" value={`$${fmt(s.execution.take_profit)}`} color="#10B981" testId="plan-tp" />
        </section>
      )}

      {/* pipeline */}
      <section className="glass rounded-3xl p-5">
        <h2 className="font-display text-lg font-bold mb-4">Prediction Pipeline</h2>
        <div className="flex flex-col">
          {pipeline.map((step, i) => {
            const Icon = STEP_ICONS[i];
            return (
              <div key={i} data-testid={step.testId} className="flex gap-4 pb-5 last:pb-0 relative">
                {i < pipeline.length - 1 && <div className="absolute left-[18px] top-9 bottom-0 w-px bg-white/10" />}
                <div className="shrink-0 w-9 h-9 rounded-full grid place-items-center z-10"
                  style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)" }}>
                  <Icon size={16} className="text-[#F59E0B]" />
                </div>
                <div className="min-w-0 pt-1">
                  <div className="font-display font-semibold text-sm">{step.title}</div>
                  <div className="text-xs text-[#9CA3AF] mt-0.5 leading-relaxed">{step.body}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* AI analysis */}
      <AnalysisCard refreshKey={refreshKey} />

      {/* top contributors */}
      <section className="glass rounded-3xl p-5 relative overflow-hidden">
        <h2 className="font-display text-lg font-bold mb-1">Biggest Data Surprises</h2>
        <p className="text-[10px] text-[#6B7280] font-mono-num mb-3">Red = stronger/hotter than expected → bad for gold · Green = weaker/cooler → good for gold</p>
        {locked && <LockedOverlay label="Unlock Contributors" />}
        <div className={`flex flex-col gap-2 ${locked ? "blur-[5px] select-none pointer-events-none" : ""}`}>
          {(locked ? LOCKED_CONTRIBS : s.top_contributors).map((c) => {
            const hawkish = c.contribution >= 0;
            return (
              <div key={c.code} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num">{c.category}</div>
                </div>
                <span className="font-mono-num text-sm font-semibold"
                  style={{ color: hawkish ? "#EF4444" : "#10B981" }}>
                  {hawkish ? "Bad for gold" : "Good for gold"} {c.contribution > 0 ? "+" : ""}{c.contribution}
                </span>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function DriverChip({ label, t, color, unit }) {
  return (
    <div className="glass rounded-2xl p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num">{label}</div>
      <div className="font-mono-num text-lg font-bold mt-0.5">{fmt(t.value, unit)}</div>
      <div className={`text-[10px] font-mono-num ${dirColor(t.direction)}`}>{t.change > 0 ? "+" : ""}{t.change}</div>
      <div className="mt-2 h-0.5 rounded-full w-full" style={{ background: `${color}55` }} />
    </div>
  );
}

function PlanCell({ label, value, color, testId }) {
  return (
    <div data-testid={testId} className="glass rounded-2xl p-3 text-center">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono-num">{label}</div>
      <div className="font-mono-num text-base font-bold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function Loading() {
  return <div className="text-center text-[#6B7280] py-20 font-mono-num">Loading signal…</div>;
}
